"use client";

import {
    FC,
    ForwardRefExoticComponent,
    PropsWithChildren,
    RefAttributes,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import {
    splitArrIntoChunks,
    useRuntimeConfig,
    useLayoutProfileConfig,
    ChatContext,
    useSearchParamsQuery,
    formatSearchParamsToString,
} from "@finbackoffice/site-core";
import { CrmContext, IModalForwardRefPropsV2 } from "contexts";
import { ILayoutsProfileSection, ProfileLayoutSectionEnum } from "@finbackoffice/fe-core";
import { useParams, usePathname, useRouter } from "next/navigation";
import Messages from "components/account/messages/Messages";
import ComposeMessage from "components/account/messages/compose/ComposeMessage";
import Modal from "components/base/modal/Modal";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import { ModalTypes, RouterQuery } from "utils/constants";
import Button from "components/base/button/Button";
import Chat from "components/account/messages/chat/Chat";
import { ModalStyleTypeEnum } from "components/base/modal/constants/modal-config";
import styles from "./sections.module.sass";

type IMessagesSectionModalProps = {
    handleClose?: () => void;
    onClose?: () => void;
};

const MessagesSectionModal: ForwardRefExoticComponent<
    IMessagesSectionModalProps & PropsWithChildren & RefAttributes<IModalForwardRefPropsV2>
> = forwardRef(({ children, onClose, handleClose }, ref) => {
    return (
        <Modal
            priority
            ref={ref}
            type={ModalTypes.ACCOUNT_MESSAGES}
            styleClass={styles.profileDetailsModal}
            styleConfigType={ModalStyleTypeEnum.ProfileDetails}
            animateVariant="bottom"
            closeButton
            onClose={onClose}>
            <div data-testid="account-message-content" className={styles.messagesContainer}>
                {children}
                <Button
                    name="close"
                    type="button"
                    className={styles.closeButton}
                    onClick={handleClose}>
                    <Translate tid="base_close" />
                </Button>
            </div>
        </Modal>
    );
});

const MessagesSection: FC = () => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const router = useRouter();
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();
    const params = useParams();
    const { msgType: msgTypeParam, id } = query;

    const { unreadMessagesCount, openInbox } = useContext(CrmContext);
    const { unreadMessages, chatStats } = useContext(ChatContext);
    const [msgType, setMsgType] = useState<ProfileLayoutSectionEnum | RouterQuery>(
        (msgTypeParam as ProfileLayoutSectionEnum | RouterQuery) || params?.msgType || "",
    );
    const messagesModalRef = useRef<IModalForwardRefPropsV2>(null);
    const { config: messagesLayoutConfig } = useLayoutProfileConfig(
        ProfileLayoutSectionEnum.Messages,
    );
    const messagesLayoutConfigMemo: ILayoutsProfileSection | null = useMemo(() => {
        const result = structuredClone(messagesLayoutConfig ?? null);

        if (result?.config) {
            if (COMMON_SITE_CONFIGS.crm.enable) {
                result.config.push({
                    name: ProfileLayoutSectionEnum.Crm,
                    enable: true,
                });
            }
        }

        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const enabledSectionsConfig = useMemo(
        () => messagesLayoutConfigMemo?.config?.filter((config) => config.enable),
        [messagesLayoutConfigMemo?.config],
    );
    const unreadMessagesMemo = useMemo(
        () => unreadMessages.length + (chatStats?.unread_message ?? 0),
        [chatStats?.unread_message, unreadMessages.length],
    );

    const sectionConfigChunks: ILayoutsProfileSection[][] = useMemo(
        () => splitArrIntoChunks(enabledSectionsConfig ?? [], 2),
        [enabledSectionsConfig],
    );

    useEffect(() => {
        if (msgType && messagesModalRef.current && !!renderContent) {
            messagesModalRef.current?.open();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (msgTypeParam && msgTypeParam !== msgType && messagesModalRef.current) {
            setMsgType(msgTypeParam as ProfileLayoutSectionEnum | RouterQuery);
            messagesModalRef.current?.open();
        }
    }, [msgTypeParam, msgType]);

    const updateScope = useCallback((val: ProfileLayoutSectionEnum | RouterQuery) => {
        setMsgType(val);
        messagesModalRef.current?.open();
    }, []);

    const renderItem = useCallback(
        (item: ILayoutsProfileSection) => {
            let content;
            switch (item.name) {
                case ProfileLayoutSectionEnum.Inbox:
                    content = (
                        <Button
                            type="button"
                            key={item.name}
                            variant="custom"
                            onClick={() => updateScope(ProfileLayoutSectionEnum.Inbox)}
                            data-testid="account-menu-item-inbox">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages-inbox.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            {Boolean(unreadMessagesMemo) && (
                                <span className={styles.unreadMsgsCount}>{unreadMessagesMemo}</span>
                            )}
                            <p>
                                <Translate
                                    tid="account_menu_messages_inbox"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Sent:
                    content = (
                        <Button
                            type="button"
                            key={item.name}
                            variant="custom"
                            onClick={() => updateScope(ProfileLayoutSectionEnum.Sent)}
                            data-testid="account-menu-item-sent">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages-sent.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_messages_sent"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Compose:
                    content = (
                        <Button
                            type="button"
                            key={item.name}
                            variant="custom"
                            onClick={() => updateScope(RouterQuery.Compose)}
                            data-testid="account-menu-item-compose">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages-compose.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_messages_compose"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Crm:
                    content = (
                        <Button key={item.name} type="button" onClick={() => openInbox()}>
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/messages-notifications.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            {unreadMessagesCount > 0 && (
                                <i className={styles.notificationsCount}>{unreadMessagesCount}</i>
                            )}
                            <Translate
                                tid="account_menu_notifications_heading"
                                namespace={TranslationScopes.UserAccount}
                            />
                        </Button>
                    );
                    break;
                default:
                    return null;
            }

            return item.enable ? content : null;
        },
        [unreadMessagesMemo, unreadMessagesCount, updateScope, openInbox],
    );

    const onMessagesModalClose = useCallback(() => {
        const q = { ...query };
        if (msgTypeParam || q.id) {
            if (msgTypeParam) {
                delete q.msgType;
            }
            if (q.id) {
                delete q.id;
            }
            router.push(`${pathname}?${formatSearchParamsToString(q)}`);
        }
    }, [msgTypeParam, query, router, pathname]);

    const renderContent = useMemo(() => {
        switch (msgType) {
            case RouterQuery.Compose:
                return <ComposeMessage />;
            case RouterQuery.Inbox:
            case RouterQuery.Sent:
                if (id) {
                    return <Chat />;
                }
                return <Messages msgType={msgType} />;
            default:
                return null;
        }
    }, [msgType, id]);

    return (
        <>
            <nav className={styles.subMenu} data-testid="account-messages-tab-container">
                {sectionConfigChunks.map((chunk, index) =>
                    chunk.length ? <li key={index}>{chunk.map(renderItem)}</li> : null,
                )}
            </nav>
            <MessagesSectionModal
                onClose={onMessagesModalClose}
                handleClose={() => {
                    void messagesModalRef.current?.close();
                }}
                ref={messagesModalRef}>
                {renderContent}
            </MessagesSectionModal>
        </>
    );
};

export default MessagesSection;
